import React, { useState, useCallback } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useWorkoutPlanCreation } from '../../../context/WorkoutPlanContext';
import WorkoutSelectionModal from './WorkoutSelectionModal';
import WorkoutTile from '../workoutTile';

const ScheduleTab = ({ isView }) => {
  const { planData, updatePlanData } = useWorkoutPlanCreation();
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);

  const schedule = planData.schedule || [];

  // Add a new day
  const handleAddDay = useCallback(() => {
    const newDay = {
      day_number: schedule.length + 1,
      workouts: []
    };
    updatePlanData({
      schedule: [...schedule, newDay]
    });
  }, [schedule, updatePlanData]);

  // Remove a day
  const handleRemoveDay = useCallback(
    (dayIndex) => {
      const updatedSchedule = schedule
        .filter((_, idx) => idx !== dayIndex)
        .map((day, idx) => ({
          ...day,
          day_number: idx + 1
        }));
      updatePlanData({
        schedule: updatedSchedule
      });
    },
    [schedule, updatePlanData]
  );

  // Open workout selection modal for a specific day
  const handleAddWorkout = useCallback((dayIndex) => {
    setSelectedDayIndex(dayIndex);
    setShowWorkoutModal(true);
  }, []);

  // Handle workout selection from modal — replace the day's workouts with the full selection
  const handleWorkoutsSelected = useCallback(
    (selectedWorkouts) => {
      if (selectedDayIndex === null) return;

      const updatedSchedule = [...schedule];
      updatedSchedule[selectedDayIndex] = {
        ...updatedSchedule[selectedDayIndex],
        workouts: selectedWorkouts
      };

      updatePlanData({
        schedule: updatedSchedule
      });

      setShowWorkoutModal(false);
      setSelectedDayIndex(null);
    },
    [selectedDayIndex, schedule, updatePlanData]
  );

  // Remove a workout from a day
  const handleRemoveWorkout = useCallback(
    (dayIndex, workoutIndex) => {
      const updatedSchedule = [...schedule];
      updatedSchedule[dayIndex].workouts = updatedSchedule[dayIndex].workouts.filter(
        (_, idx) => idx !== workoutIndex
      );
      updatePlanData({
        schedule: updatedSchedule
      });
    },
    [schedule, updatePlanData]
  );

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule</h3>
        {!isView && (
          <button
            type="button"
            onClick={handleAddDay}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5" />
            Add workout day
          </button>
        )}
      </div>

      {/* Workout Days Section */}
      <div className="space-y-6">
        {schedule.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-shoorah-darkBgColor rounded-lg">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No workout days added yet. Click "Add workout day" to get started.
            </p>
          </div>
        ) : (
          schedule.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className="border border-gray-200 dark:border-shoorah-darkBorder rounded-lg p-6"
            >
              {/* Day Header */}
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  Day {day.day_number}
                  {!isView && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDay(dayIndex)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove day"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </h4>
              </div>

              {/* Workouts Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {day.workouts && day.workouts.length > 0 && (
                  <>
                    {day.workouts.map((workout, workoutIndex) => (
                      <div key={workoutIndex} className="relative">
                        <WorkoutTile
                          image={workout.cover_image_url}
                          title={workout.workout_name}
                          usageCount={workout.usage_count || 0}
                          badgeText={workout.difficulty || 'Workout'}
                          onClick={() => {}}
                          onMenuClick={(e) => e.stopPropagation()}
                        />
                        {!isView && (
                          <button
                            type="button"
                            onClick={() => handleRemoveWorkout(dayIndex, workoutIndex)}
                            className="absolute top-2 right-2 bg-white dark:bg-shoorah-darkBgColor rounded-full p-1.5 shadow-md text-red-500 hover:text-red-700 hover:bg-red-50 z-10"
                            title="Remove workout"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {/* Add Workout Tile - Always shown at the end if not in view mode */}
                {!isView && (
                  <button
                    type="button"
                    onClick={() => handleAddWorkout(dayIndex)}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-shoorah-primary hover:bg-gray-50 dark:hover:bg-shoorah-darkBgColor transition-colors flex flex-col items-center justify-center gap-3 h-full min-h-[180px]"
                  >
                    <PlusIcon className="w-10 h-10 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Add Workout
                    </span>
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Workout Selection Modal */}
      <WorkoutSelectionModal
        isOpen={showWorkoutModal}
        onClose={() => {
          setShowWorkoutModal(false);
          setSelectedDayIndex(null);
        }}
        onSelectWorkouts={handleWorkoutsSelected}
        preSelectedWorkouts={
          selectedDayIndex !== null ? schedule[selectedDayIndex]?.workouts || [] : []
        }
      />
    </div>
  );
};

export default ScheduleTab;
